import { audio } from '../systems/audio/AudioSystem.js';
import { AuthClient } from '../systems/net/AuthClient.js';

/**
 * Account screen: Sign Up / Log In (Supabase-backed — echoes, shards, and
 * shop upgrades sync to the account) when signed out, or a Log Out button
 * plus status when signed in. Signed-out players can also set a local
 * guest display name (no account, no sync) so they can still opt into
 * leaderboard submissions — the same lightweight thing this screen used to
 * do before accounts existed.
 */
export class LoginMenu {
  constructor(root, save, { onBack, onStatistics, cloudSync }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.onStatistics = onStatistics;
    this.cloudSync = cloudSync;
    this.authTab = 'login';
    this.error = '';
    this.notice = '';
    this.busy = false;
    this._destroyed = false;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay';
    this._render();
    this.root.appendChild(this.el);

    // main.js kicks off its own session restore at boot, which may still be
    // in flight when this screen opens — render as a guest immediately
    // (the common case) and upgrade in place if a session turns up.
    if (!this.save.accountUsername) {
      AuthClient.currentUsername().then((username) => {
        if (username && !this._destroyed) {
          this.save.accountUsername = username;
          this._render();
        }
      });
    }
  }

  _render() {
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'PROFILE';
    this.el.appendChild(title);

    if (this.save.accountUsername) {
      this._renderAuthed();
    } else {
      this._renderGuest();
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  _renderAuthed() {
    const subtitle = document.createElement('div');
    subtitle.className = 'menu-subtitle';
    subtitle.textContent = 'Echoes, Shards, and shop upgrades sync to this account.';
    this.el.appendChild(subtitle);

    const status = document.createElement('div');
    status.className = 'login-status';
    status.textContent = `Logged in as ${this.save.accountUsername}`;
    this.el.appendChild(status);

    this._appendStatisticsButton();

    const logoutBtn = document.createElement('button');
    logoutBtn.type = 'button';
    logoutBtn.className = 'menu-button menu-button-primary';
    logoutBtn.textContent = 'Log Out';
    logoutBtn.disabled = this.busy;
    logoutBtn.addEventListener('click', () => this._handleLogOut());
    this.el.appendChild(logoutBtn);
  }

  _renderGuest() {
    if (AuthClient.isConfigured) {
      this._renderAuthTabs();
      if (this.notice) {
        const notice = document.createElement('div');
        notice.className = 'login-status';
        notice.textContent = this.notice;
        this.el.appendChild(notice);
      }
      this._renderAuthForm();
    }
    this._renderGuestNameSection();
    this._appendStatisticsButton();
  }

  _appendStatisticsButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'menu-button';
    button.textContent = 'View Statistics';
    button.addEventListener('click', () => { audio.playSfx('click'); this.onStatistics(); });
    this.el.appendChild(button);
  }

  _renderAuthTabs() {
    const tabs = document.createElement('div');
    tabs.className = 'login-tabs';

    const loginTab = document.createElement('button');
    loginTab.type = 'button';
    loginTab.className = `login-tab${this.authTab === 'login' ? ' login-tab-active' : ''}`;
    loginTab.textContent = 'Log In';
    loginTab.addEventListener('click', () => { this.authTab = 'login'; this.error = ''; this._render(); });
    tabs.appendChild(loginTab);

    const signupTab = document.createElement('button');
    signupTab.type = 'button';
    signupTab.className = `login-tab${this.authTab === 'signup' ? ' login-tab-active' : ''}`;
    signupTab.textContent = 'Sign Up';
    signupTab.addEventListener('click', () => { this.authTab = 'signup'; this.error = ''; this._render(); });
    tabs.appendChild(signupTab);

    this.el.appendChild(tabs);
  }

  _makeInput(type, placeholder, autocomplete) {
    const input = document.createElement('input');
    input.type = type;
    input.className = 'login-name-input';
    input.placeholder = placeholder;
    if (autocomplete) input.autocomplete = autocomplete;
    return input;
  }

  _renderAuthForm() {
    const form = document.createElement('form');
    form.className = 'settings-rows';

    if (this.authTab === 'signup') {
      const usernameInput = this._makeInput('text', 'Username (3-20 chars)', 'username');
      usernameInput.maxLength = 20;
      form.appendChild(usernameInput);

      const emailInput = this._makeInput('email', 'Email', 'email');
      form.appendChild(emailInput);

      const passwordInput = this._makeInput('password', 'Password', 'new-password');
      form.appendChild(passwordInput);

      const confirmInput = this._makeInput('password', 'Confirm password', 'new-password');
      form.appendChild(confirmInput);

      const submit = document.createElement('button');
      submit.type = 'submit';
      submit.className = 'menu-button menu-button-primary';
      submit.textContent = 'Sign Up';
      submit.disabled = this.busy;
      form.appendChild(submit);

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleSignUp({
          username: usernameInput.value,
          email: emailInput.value,
          password: passwordInput.value,
          confirmPassword: confirmInput.value,
        });
      });
    } else {
      const identifierInput = this._makeInput('text', 'Username or email', 'username');
      form.appendChild(identifierInput);

      const passwordInput = this._makeInput('password', 'Password', 'current-password');
      form.appendChild(passwordInput);

      const submit = document.createElement('button');
      submit.type = 'submit';
      submit.className = 'menu-button menu-button-primary';
      submit.textContent = 'Log In';
      submit.disabled = this.busy;
      form.appendChild(submit);

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleLogIn({ identifier: identifierInput.value, password: passwordInput.value });
      });
    }

    this.el.appendChild(form);

    if (this.error) {
      const error = document.createElement('div');
      error.className = 'login-error';
      error.textContent = this.error;
      this.el.appendChild(error);
    }
  }

  _renderGuestNameSection() {
    const divider = document.createElement('div');
    divider.className = 'menu-subtitle';
    divider.textContent = AuthClient.isConfigured ? 'or play as a guest' : 'Set a display name to submit runs to the global leaderboard.';
    this.el.appendChild(divider);

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'login-name-input';
    input.maxLength = 20;
    input.placeholder = 'Guest display name';
    input.value = this.save.profileName;
    this.el.appendChild(input);

    const status = document.createElement('div');
    status.className = 'login-status';
    status.textContent = this.save.profileName ? `Playing as guest "${this.save.profileName}"` : 'No display name set — runs will not be submitted.';
    this.el.appendChild(status);

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'menu-button';
    saveBtn.textContent = 'Save Name';
    saveBtn.addEventListener('click', () => {
      audio.playSfx('click');
      this.save.setProfileName(input.value);
      this._render();
    });
    this.el.appendChild(saveBtn);
  }

  async _handleSignUp({ username, email, password, confirmPassword }) {
    if (!username.trim() || !email.trim() || !password) {
      this.error = 'Fill in every field.';
      this._render();
      return;
    }
    if (password !== confirmPassword) {
      this.error = 'Passwords do not match.';
      this._render();
      return;
    }
    if (password.length < 6) {
      this.error = 'Password must be at least 6 characters.';
      this._render();
      return;
    }

    audio.playSfx('click');
    this.busy = true;
    this.error = '';
    this.notice = '';
    this._render();

    const result = await AuthClient.signUp({ username, email, password });
    this.busy = false;

    if (!result.ok) {
      this.error = result.error;
      this._render();
      return;
    }

    if (!result.confirmed) {
      this.notice = 'Check your email to confirm your account, then log in.';
      this.authTab = 'login';
      this._render();
      return;
    }

    await this.cloudSync.onSignedIn(result.userId, result.username, { seedFromLocal: true });
    this._render();
  }

  async _handleLogIn({ identifier, password }) {
    if (!identifier.trim() || !password) {
      this.error = 'Fill in every field.';
      this._render();
      return;
    }

    audio.playSfx('click');
    this.busy = true;
    this.error = '';
    this.notice = '';
    this._render();

    const result = await AuthClient.logIn({ identifier, password });
    this.busy = false;

    if (!result.ok) {
      this.error = result.error;
      this._render();
      return;
    }

    await this.cloudSync.onSignedIn(result.userId, result.username, { seedFromLocal: false });
    this._render();
  }

  async _handleLogOut() {
    audio.playSfx('click');
    this.busy = true;
    this._render();
    await AuthClient.logOut();
    this.cloudSync.onSignedOut();
    this.busy = false;
    this._render();
  }

  destroy() {
    this._destroyed = true;
    this.el.remove();
  }
}
